// src/pages/Admin/DepensesRecettes/AddDepenseRecetteModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import {
  createDepenseRecetteClub,
  getDepensesRecettesClub,
} from "../../../services/depensesrecettesclubServices";
import { getSettings } from "../../../services/settingsServices";
import { getAllPlanComptables } from "../../../services/plancomptableServices";

const todayStr = () => new Date().toISOString().slice(0, 10);

const REGLEMENTS = [
  "Virement",
  "Carte bancaire",
  "Espèces",
  "Chèque",
  "Prélèvement",
  "HelloAsso",
  "Chèques Vacances",
  "Coupons Sports",
  "Autre…",
];

function neighborsFromSeason(season) {
  const m = /^(\d{4})\s*-\s*(\d{4})$/.exec(season || "");
  if (!m)
    return { prev: season || "", current: season || "", next: season || "" };
  const y1 = parseInt(m[1], 10);
  const y2 = parseInt(m[2], 10);
  return {
    prev: `${y1 - 1}-${y2 - 1}`,
    current: `${y1}-${y2}`,
    next: `${y1 + 1}-${y2 + 1}`,
  };
}

export default function AddDepenseRecetteModal({
  isOpen,
  onClose,
  refreshList,
}) {
  const [formData, setFormData] = useState({
    saison: "",
    date_operation: todayStr(),
    designation: "",
    categorie: "",
    beneficiaire: "",
    montant: "",
    type_reglement: "",
    type_operation: "Dépense",
    photo_url: "",
    lien_url: "",
    plan_comptable_id: "",
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [saisonActive, setSaisonActive] = useState("");
  const [seasonOptions, setSeasonOptions] = useState([]);

  const [categories, setCategories] = useState([]);
  const [useNewCategorie, setUseNewCategorie] = useState(false);
  const [newCategorie, setNewCategorie] = useState("");

  const [useOtherReglement, setUseOtherReglement] = useState(false);
  const [otherReglement, setOtherReglement] = useState("");

  const [imgOk, setImgOk] = useState(true);

  const [planComptables, setPlanComptables] = useState([]);

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const settingsData = await getSettings();
      const active = settingsData?.[0]?.saison_active || "";
      setSaisonActive(active);
      const { prev, current, next } = neighborsFromSeason(active || "");
      setSeasonOptions(
        Array.from(new Set([prev, current, next].filter(Boolean)))
      );

      const ops = await getDepensesRecettesClub();
      const cats = Array.from(
        new Set(
          (Array.isArray(ops) ? ops : [])
            .map((o) => o.categorie)
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, "fr"));
      setCategories(cats);

      // 🔸 Charger les comptes comptables
      const planData = await getAllPlanComptables();
      const sortedPlan = (Array.isArray(planData) ? planData : []).sort((a, b) => {
        if (a.sens === b.sens) return a.ordre - b.ordre;
        return a.sens === "RECETTE" ? -1 : 1;
      });
      setPlanComptables(sortedPlan);

      setFormData((prev) => ({
        ...prev,
        saison: current || active || prev.saison || "",
        date_operation: prev.date_operation || todayStr(),
      }));

      setUseNewCategorie(false);
      setNewCategorie("");
      setUseOtherReglement(false);
      setOtherReglement("");
      setImgOk(true);
    } catch (e) {
      console.error(e);
      toast.error(
        "Impossible de charger la configuration (saison / catégories / plan comptable)."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      saison: "",
      date_operation: todayStr(),
      designation: "",
      categorie: "",
      beneficiaire: "",
      montant: "",
      type_reglement: "",
      type_operation: "Dépense",
      photo_url: "",
      lien_url: "",
      plan_comptable_id: "",
    });
    loadContext();
  }, [isOpen, loadContext]);

  const canSubmit = useMemo(() => {
    const hasSeason = !!formData.saison;
    const hasDate = !!formData.date_operation;
    const hasDesignation = !!formData.designation?.trim();
    const hasCategorie = useNewCategorie
      ? !!newCategorie?.trim()
      : !!formData.categorie;
    const hasMontant =
      formData.montant !== "" && !Number.isNaN(Number(formData.montant));
    const hasReglement = useOtherReglement
      ? !!otherReglement?.trim()
      : !!formData.type_reglement;
    return (
      !loading &&
      !submitting &&
      hasSeason &&
      hasDate &&
      hasDesignation &&
      hasCategorie &&
      hasMontant &&
      hasReglement
    );
  }, [
    formData,
    newCategorie,
    otherReglement,
    useNewCategorie,
    useOtherReglement,
    loading,
    submitting,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "categorie") {
      if (value === "__new__") {
        setUseNewCategorie(true);
        setFormData((p) => ({ ...p, categorie: "" }));
      } else {
        setUseNewCategorie(false);
        setNewCategorie("");
        setFormData((p) => ({ ...p, categorie: value }));
      }
      return;
    }
    if (name === "type_reglement") {
      if (value === "__other__") {
        setUseOtherReglement(true);
        setFormData((p) => ({ ...p, type_reglement: "" }));
      } else {
        setUseOtherReglement(false);
        setOtherReglement("");
        setFormData((p) => ({ ...p, type_reglement: value }));
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ⛔ Empêche la soumission via "Enter" dans certains champs
  const preventEnterSubmit = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        categorie: useNewCategorie ? newCategorie.trim() : formData.categorie,
        type_reglement: useOtherReglement
          ? otherReglement.trim()
          : formData.type_reglement,
        montant: Number(formData.montant),
      };
      await createDepenseRecetteClub(payload);
      toast.success("Opération enregistrée avec succès");
      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'opération", error);
      toast.error("Erreur lors de l'ajout de l'opération");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal container -> hauteur maîtrisée + layout colonne */}
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70 max-h-[85vh] flex flex-col">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Ajouter une dépense / recette
          </h3>
          <p className="text-white/90 text-center text-xs mt-1">
            Saisissez les informations ci-dessous.
          </p>
        </div>

        {/* Form: contenu scrollable + barre d'actions sticky */}
        <form onSubmit={handleSubmit} className="relative">
          {/* Zone scrollable */}
          <div className="p-5 sm:p-6 space-y-5 max-h-[80vh] overflow-y-auto">
            {/* Saison + Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Saison
                </label>
                <select
                  name="saison"
                  value={formData.saison}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required
                >
                  <option value="">
                    {loading ? "Chargement…" : "— Sélectionner —"}
                  </option>
                  {seasonOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Date de l’opération
                </label>
                <input
                  type="date"
                  name="date_operation"
                  value={formData.date_operation}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required
                />
              </div>
            </div>

            {/* Désignation + Bénéficiaire */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Désignation
                </label>
                <input
                  type="text"
                  name="designation"
                  placeholder="Ex : Achat ballons, Subvention mairie…"
                  value={formData.designation}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Bénéficiaire
                </label>
                <input
                  type="text"
                  name="beneficiaire"
                  placeholder="Fournisseur / Organisme / Personne"
                  value={formData.beneficiaire}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required
                />
              </div>
            </div>

            {/* Catégorie + Type d’opération */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Catégorie
                </label>
                <select
                  name="categorie"
                  value={useNewCategorie ? "__new__" : formData.categorie}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required={!useNewCategorie}
                >
                  <option value="">
                    {loading ? "Chargement…" : "— Sélectionner —"}
                  </option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                  <option value="__new__">— Nouvelle catégorie…</option>
                </select>

                {useNewCategorie && (
                  <input
                    type="text"
                    placeholder="Saisir une nouvelle catégorie"
                    value={newCategorie}
                    onChange={(e) => setNewCategorie(e.target.value)}
                    onKeyDown={preventEnterSubmit}
                    autoFocus
                    className="mt-2 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    required
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Type d’opération
                </label>
                <select
                  name="type_operation"
                  value={formData.type_operation}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required
                >
                  <option value="Dépense">Dépense</option>
                  <option value="Recette">Recette</option>
                </select>
              </div>
            </div>

            {/* ✅ Sélecteur Plan Comptable */}
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Compte comptable
              </label>
              <select
                name="plan_comptable_id"
                value={formData.plan_comptable_id}
                onChange={handleChange}
                disabled={loading}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">— Sélectionner un compte —</option>
                {planComptables.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} — {p.intitule}
                  </option>
                ))}
              </select>
            </div>

            {/* Montant + Type de règlement */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Montant (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="montant"
                  placeholder="0,00"
                  value={formData.montant}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Type de règlement
                </label>
                <select
                  name="type_reglement"
                  value={
                    useOtherReglement ? "__other__" : formData.type_reglement
                  }
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required={!useOtherReglement}
                >
                  <option value="">— Sélectionner —</option>
                  {REGLEMENTS.map((r) => (
                    <option key={r} value={r === "Autre…" ? "__other__" : r}>
                      {r}
                    </option>
                  ))}
                </select>

                {useOtherReglement && (
                  <input
                    type="text"
                    placeholder="Précisez le type de règlement"
                    value={otherReglement}
                    onChange={(e) => setOtherReglement(e.target.value)}
                    onKeyDown={preventEnterSubmit}
                    autoFocus
                    className="mt-2 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    required
                  />
                )}
              </div>
            </div>

            {/* Photo + Lien justificatif */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  URL de la photo (optionnel)
                </label>
                <input
                  type="url"
                  name="photo_url"
                  placeholder="https://…"
                  value={formData.photo_url}
                  onChange={(e) => {
                    setImgOk(true);
                    handleChange(e);
                  }}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                {!!formData.photo_url && (
                  <div className="mt-3 flex items-center gap-3">
                    <img
                      src={formData.photo_url}
                      alt="Aperçu"
                      onError={() => setImgOk(false)}
                      className="h-16 w-16 rounded-lg object-cover ring-2 ring-slate-200 dark:ring-slate-700"
                    />
                    <div className="text-xs text-slate-600 dark:text-slate-300">
                      {imgOk ? "Aperçu de la photo" : "Impossible de charger l’image"}
                    </div>
                  </div>
                )}
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Collez une URL valide (JPG/PNG/WEBP). <strong>Astuce</strong> : vous pourrez
                  <em> téléverser</em> une image depuis la fenêtre d’édition après création.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Lien (justificatif, facture…)
                </label>
                <input
                  type="url"
                  name="lien_url"
                  placeholder="https://…"
                  value={formData.lien_url}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>
          </div>

          {/* Barre d’actions sticky (hors zone scrollée) */}
          <div className="sticky bottom-0 -mx-5 sm:-mx-6 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-t border-slate-200 dark:border-slate-800 px-5 sm:px-6 py-3 flex justify-end gap-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={!canSubmit}
              className={`!rounded-xl gap-2 ${!canSubmit ? "opacity-60 pointer-events-none" : ""}`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
